import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import SharedTable from "../benefitPlan/SharedTable";
import {
    fetchCognitoUserPools,
    fetchCognitoGroups,
    fetchCognitoUsersInGroup,
    fetchCognitoAttributes,
    fetchCognitoUserSearch,
} from "../../utils/apiUtils";

const SYSTEM_FIELDS = new Set(["username", "status", "enabled", "createdAt", "updatedAt"]);

const Cognito = () => {
    const [selectedPool, setSelectedPool] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [mode, setMode] = useState(null); // null | "browse" | "search"

    // Browse state
    const [selectedAttribute, setSelectedAttribute] = useState(null);
    const [searchValue, setSearchValue] = useState("");

    // Search state
    const [searchAttribute, setSearchAttribute] = useState(null);
    const [searchAttributeValue, setSearchAttributeValue] = useState("");
    const [submitted, setSubmitted] = useState(null);

    // ── Queries ──────────────────────────────────────────────────────────────
    const { data: poolsData, isLoading: isLoadingPools, isError: isPoolsError, error: poolsError } = useInfiniteQuery({
        queryKey: ["cognito-user-pools"],
        queryFn: fetchCognitoUserPools,
        initialPageParam: undefined,
        getNextPageParam: () => undefined,
        staleTime: 5 * 60 * 1000,
    });

    const { data: groupsData, isLoading: isLoadingGroups, isError: isGroupsError, error: groupsError } = useInfiniteQuery({
        queryKey: ["cognito-groups", selectedPool?.value],
        queryFn: () => fetchCognitoGroups(selectedPool.value),
        initialPageParam: undefined,
        getNextPageParam: () => undefined,
        enabled: !!selectedPool,
        staleTime: 5 * 60 * 1000,
    });

    const {
        data: usersData,
        isLoading: isLoadingUsers,
        isError: isUsersError,
        error: usersError,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useInfiniteQuery({
        queryKey: ["cognito-group-users", selectedPool?.value, selectedGroup?.value],
        queryFn: ({ pageParam }) => fetchCognitoUsersInGroup(selectedPool.value, selectedGroup.value, pageParam),
        initialPageParam: undefined,
        getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
        enabled: !!selectedPool && !!selectedGroup && mode === "browse",
        staleTime: 60 * 1000,
    });

    const { data: attributesData, isLoading: isLoadingAttributes } = useQuery({
        queryKey: ["cognito-attributes", selectedPool?.value],
        queryFn: () => fetchCognitoAttributes(selectedPool.value),
        enabled: !!selectedPool,
        staleTime: 10 * 60 * 1000,
    });

    const { data: searchData, isLoading: isSearching, isError: isSearchError, error: searchError } = useQuery({
        queryKey: ["cognito-user-search", submitted?.pool, submitted?.attribute, submitted?.value],
        queryFn: () => fetchCognitoUserSearch(submitted.pool, submitted.attribute, submitted.value),
        enabled: !!submitted,
        staleTime: 60 * 1000,
        retry: false,
    });

    // ── Derived ──────────────────────────────────────────────────────────────
    const poolOptions = poolsData?.pages[0]?.pools?.map((p) => ({
        value: p.Id,
        label: `${p.Name} (${p.Id})`,
    })) ?? [];

    const groupOptions = groupsData?.pages[0]?.groups?.map((g) => ({
        value: g.GroupName,
        label: g.GroupName,
    })) ?? [];

    const allUsers = useMemo(() => usersData?.pages.flatMap((p) => p.users) ?? [], [usersData]);

    const attributeOptions = useMemo(() => {
        const keys = new Set();
        allUsers.forEach((u) => Object.keys(u).forEach((k) => { if (!SYSTEM_FIELDS.has(k)) keys.add(k); }));
        return Array.from(keys).sort().map((k) => ({ value: k, label: k }));
    }, [allUsers]);

    const browseColumns = useMemo(() => {
        const attrKeys = attributeOptions.map((o) => o.value);
        return ["email", "username", "status", "enabled", ...attrKeys, "createdAt", "updatedAt"];
    }, [attributeOptions]);

    const filteredUsers = useMemo(() => {
        if (!selectedAttribute || !searchValue.trim()) return allUsers;
        const needle = searchValue.trim().toLowerCase();
        return allUsers.filter((u) => {
            const val = u[selectedAttribute.value];
            return val != null && String(val).toLowerCase().includes(needle);
        });
    }, [allUsers, selectedAttribute, searchValue]);

    const searchResults = searchData?.users ?? [];

    const searchColumns = useMemo(() => {
        const keys = Array.from(new Set(searchResults.flatMap(Object.keys)))
            .filter((k) => !["username", "status", "enabled", "createdAt", "updatedAt"].includes(k));
        return ["email", "username", "status", "enabled", ...keys, "createdAt", "updatedAt"];
    }, [searchResults]);

    const poolAttributeOptions = (attributesData?.attributes ?? []).map((a) => ({ value: a, label: a }));

    // ── Handlers ─────────────────────────────────────────────────────────────
    const clearBrowseFilters = () => { setSelectedAttribute(null); setSearchValue(""); };

    const clearSearch = () => { setSearchAttribute(null); setSearchAttributeValue(""); setSubmitted(null); };

    const handlePoolChange = (opt) => {
        setSelectedPool(opt);
        setSelectedGroup(null);
        setMode(null);
        clearBrowseFilters();
        clearSearch();
    };

    const handleGroupChange = (opt) => {
        setSelectedGroup(opt);
        clearBrowseFilters();
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
        if (newMode === "browse") clearSearch();
        if (newMode === "search") { setSelectedGroup(null); clearBrowseFilters(); }
    };

    const handleSearch = () => {
        if (!selectedPool || !searchAttribute || !searchAttributeValue.trim()) return;
        setSubmitted({ pool: selectedPool.value, attribute: searchAttribute.value, value: searchAttributeValue.trim() });
    };

    const canSearch = !!selectedPool && !!searchAttribute && !!searchAttributeValue.trim();

    return (
        <div className="container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <p className="title">Cognito</p>
            </div>

            {/* ── Pool selector ───────────────────────────────────────────────── */}
            <div style={{ padding: "1rem 0rem" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: ".6rem", minWidth: 280 }}>
                    <label style={{ fontSize: ".9rem", color: "var(--darkGrey)" }}>User Pool</label>
                    {isPoolsError ? (
                        <span className="danger">Failed to load user pools: {poolsError.message}</span>
                    ) : (
                        <Select
                            name="userPool"
                            className="react-select-container large-input"
                            classNamePrefix="react-select"
                            options={poolOptions}
                            value={selectedPool}
                            onChange={handlePoolChange}
                            isLoading={isLoadingPools}
                            placeholder="Select a user pool..."
                            isClearable
                        />
                    )}
                </div>
            </div>

            {/* ── Mode radio — appears after pool is selected ─────────────────── */}
            {selectedPool && (
                <div style={{ display: "flex", gap: "1.5rem", padding: "0 0 1.25rem 0" }}>
                    {[
                        { value: "browse", label: "Search by group" },
                        { value: "search", label: "Search in User Pool" },
                    ].map(({ value, label }) => (
                        <label key={value} style={{ display: "flex", alignItems: "center", gap: ".4rem", cursor: "pointer", fontSize: ".95rem", color: "var(--darkGrey)" }}>
                            <input
                                type="radio"
                                name="cognitoMode"
                                value={value}
                                checked={mode === value}
                                onChange={() => handleModeChange(value)}
                            />
                            {label}
                        </label>
                    ))}
                </div>
            )}

            {/* ── Search by group (browse) ─────────────────────────────────────── */}
            {mode === "browse" && (
                <>
                    {/* Group selector */}
                    <div style={{ display: "flex", alignItems: "flex-end", gap: "1rem", flexWrap: "wrap", paddingBottom: "1rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: ".6rem", minWidth: 240 }}>
                            <label style={{ fontSize: ".9rem", color: "var(--darkGrey)" }}>Group</label>
                            {isGroupsError ? (
                                <span className="danger">Failed to load groups: {groupsError.message}</span>
                            ) : (
                                <Select
                                    name="cognitoGroup"
                                    className="react-select-container large-input"
                                    classNamePrefix="react-select"
                                    options={groupOptions}
                                    value={selectedGroup}
                                    onChange={handleGroupChange}
                                    isLoading={isLoadingGroups}
                                    placeholder="Select a group..."
                                    isClearable
                                />
                            )}
                        </div>

                        {/* Attribute + value filters — shown once first page loads */}
                        {selectedGroup && !isLoadingUsers && attributeOptions.length > 0 && (
                            <>
                                <div style={{ display: "flex", flexDirection: "column", gap: ".6rem", minWidth: 200 }}>
                                    <label style={{ fontSize: ".9rem", color: "var(--darkGrey)" }}>Filter by attribute</label>
                                    <Select
                                        name="attributeFilter"
                                        className="react-select-container large-input"
                                        classNamePrefix="react-select"
                                        options={attributeOptions}
                                        value={selectedAttribute}
                                        onChange={(opt) => { setSelectedAttribute(opt); setSearchValue(""); }}
                                        placeholder="Select attribute..."
                                        isClearable
                                    />
                                </div>
                                {selectedAttribute && (
                                    <div style={{ display: "flex", flexDirection: "column", gap: ".6rem" }}>
                                        <label style={{ fontSize: ".9rem", color: "var(--darkGrey)" }}>Value</label>
                                        <input
                                            className="date-picker"
                                            style={{ minWidth: "200px", height: "50px", fontSize: "1rem" }}
                                            type="text"
                                            placeholder={`Filter ${selectedAttribute.label}...`}
                                            value={searchValue}
                                            autoComplete="off"
                                            onChange={(e) => setSearchValue(e.target.value)}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {selectedGroup && !isLoadingUsers && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 0 1rem 0" }}>
                            <p style={{ fontSize: ".9rem", color: "var(--darkGrey)" }}>
                                {filteredUsers.length} of {allUsers.length} user{allUsers.length !== 1 ? "s" : ""}
                                {selectedAttribute && searchValue ? ` matching ${selectedAttribute.label} = "${searchValue}"` : ""}
                                {hasNextPage ? " (more to load)" : ""}
                            </p>
                            {(selectedAttribute || searchValue) && (
                                <p style={{ cursor: "pointer", color: "var(--red)", fontSize: ".9rem" }} onClick={clearBrowseFilters}>
                                    Clear filters
                                </p>
                            )}
                        </div>
                    )}

                    {isUsersError && <span className="danger">Failed to load users: {usersError.message}</span>}

                    {selectedGroup && (
                        isLoadingUsers ? <p>Loading...</p> : (
                            <>
                                <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                    <SharedTable
                                        type="sso"
                                        columns={browseColumns}
                                        items={filteredUsers}
                                        activeDraftId={null}
                                        hasNextPage={hasNextPage}
                                        isFetchingNextPage={isFetchingNextPage}
                                        isFetching={isFetchingNextPage}
                                        isLoading={isLoadingUsers}
                                    />
                                </div>
                                {hasNextPage && (
                                    <div style={{ display: "flex", justifyContent: "center", padding: "1rem 0" }}>
                                        <button className="btn" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                                            {isFetchingNextPage ? "Loading..." : "Load next 500"}
                                        </button>
                                    </div>
                                )}
                            </>
                        )
                    )}

                    {!selectedGroup && !isLoadingGroups && (
                        <p style={{ color: "var(--darkGrey)" }}>Select a group to view its members.</p>
                    )}
                </>
            )}

            {/* ── Search in User Pool ──────────────────────────────────────────── */}
            {mode === "search" && (
                <>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: "1rem", flexWrap: "wrap", paddingBottom: "1rem" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: ".6rem", minWidth: 220 }}>
                            <label style={{ fontSize: ".9rem", color: "var(--darkGrey)" }}>Attribute</label>
                            <CreatableSelect
                                name="searchAttribute"
                                className="react-select-container large-input"
                                classNamePrefix="react-select"
                                options={poolAttributeOptions}
                                value={searchAttribute}
                                onChange={(opt) => { setSearchAttribute(opt); setSearchAttributeValue(""); setSubmitted(null); }}
                                isLoading={isLoadingAttributes}
                                placeholder={isLoadingAttributes ? "Loading..." : "Select or type (e.g. custom:npi)..."}
                                isClearable
                                formatCreateLabel={(input) => `Search by "${input}"`}
                            />
                        </div>

                        {searchAttribute && (
                            <div style={{ display: "flex", flexDirection: "column", gap: ".6rem" }}>
                                <label style={{ fontSize: ".9rem", color: "var(--darkGrey)" }}>Value</label>
                                <div style={{ display: "flex", gap: ".5rem" }}>
                                    <input
                                        className="date-picker"
                                        style={{ minWidth: "220px", height: "50px", fontSize: "1rem" }}
                                        type="text"
                                        placeholder={`Exact value for ${searchAttribute.label}...`}
                                        value={searchAttributeValue}
                                        autoComplete="off"
                                        onChange={(e) => setSearchAttributeValue(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter" && canSearch) handleSearch(); }}
                                    />
                                    <button
                                        className="btn"
                                        style={{ height: "50px", whiteSpace: "nowrap" }}
                                        onClick={handleSearch}
                                        disabled={!canSearch}
                                    >
                                        Search
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {submitted && !isSearching && (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 0 1rem 0" }}>
                            <p style={{ fontSize: ".9rem", color: "var(--darkGrey)" }}>
                                {searchResults.length} user{searchResults.length !== 1 ? "s" : ""} where {submitted.attribute} = &quot;{submitted.value}&quot;
                            </p>
                            <p style={{ cursor: "pointer", color: "var(--red)", fontSize: ".9rem" }} onClick={clearSearch}>
                                Clear
                            </p>
                        </div>
                    )}

                    {isSearchError && <span className="danger">Search failed: {searchError.message}</span>}

                    {!submitted && !isSearching && (
                        <p style={{ color: "var(--darkGrey)", paddingTop: "0.5rem" }}>
                            {searchAttribute ? "Type a value and click Search." : "Select an attribute to search across the entire pool."}
                        </p>
                    )}

                    {submitted && (
                        isSearching ? <p>Searching pool...</p> : (
                            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                                <SharedTable
                                    type="sso"
                                    columns={searchColumns}
                                    items={searchResults}
                                    activeDraftId={null}
                                    hasNextPage={false}
                                    isFetchingNextPage={false}
                                    isFetching={false}
                                    isLoading={false}
                                />
                            </div>
                        )
                    )}
                </>
            )}

            {!selectedPool && (
                <p style={{ color: "var(--darkGrey)", paddingTop: "2rem" }}>Select a user pool to get started.</p>
            )}
        </div>
    );
};

export default Cognito;
